import { Dialog as RadixDialog } from 'radix-ui';
import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { decorativeIcon, X } from '../../lib/icons';
import { useOverlayZIndex } from '../../lib/overlayStack';
import { IconButton } from '../IconButton/IconButton';

export interface SheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Accessible name for the close control, already translated. */
  readonly closeLabel: string;
  /** Pinned to the bottom; the body scrolls under it. */
  readonly footer?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
}

/**
 * The phone bottom sheet — full-width, pinned to the bottom edge, rounded top
 * corners.
 *
 * Its only current caller is `InstallCard`'s "how to install" instructions on
 * a phone. It used to also render as a 380 px panel pinned to the END edge,
 * standing in for a right-side "detail drawer" on five admin lists — that
 * configuration is gone, along with the claim that shipped beside it: the
 * drawer's own doc comment called it "deliberately non-modal…it can stay
 * open while a supervisor arrows down the grid." That was never true. Radix
 * defaults `modal` to `true` and nothing here ever passed `modal={false}`,
 * so it rendered an overlay, `aria-hid` everything else, and trapped focus
 * exactly like every other Radix dialog — the grid was never actually
 * reachable with it open. The five admin surfaces that used it now carry
 * their state in the URL as a `Dialog`, a route, or an inline expanding row
 * instead (see each feature's own doc comment for which).
 *
 * `Esc` closes it and focus returns to whatever opened it, because it is
 * opened from a button rather than owning a `Dialog.Trigger`.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  footer,
  children,
  className,
}: SheetProps) {
  /**
   * Where focus was when the sheet opened.
   *
   * Radix restores focus on its own only when it owns the trigger. This
   * sheet is opened from a plain button, not a `Dialog.Trigger`, so without
   * this the `Esc` that closes it drops focus onto `<body>`.
   */
  const opener = useRef<HTMLElement | null>(null);
  const content = useRef<HTMLDivElement>(null);
  const { overlayZ, contentZ } = useOverlayZIndex(open);

  useEffect(() => {
    if (open) {
      opener.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-overlay" style={{ zIndex: overlayZ }} />
        <RadixDialog.Content
          ref={content}
          onOpenAutoFocus={(event) => {
            // Same rule as `Dialog`: focus the SHEET, never the first control
            // inside it — which today is the close button and tomorrow is
            // whatever action a caller puts first.
            event.preventDefault();
            content.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            const target = opener.current;
            if (target !== null && target.isConnected) {
              event.preventDefault();
              target.focus();
            }
          }}
          style={{ zIndex: contentZ }}
          className={cn(
            'fixed inset-x-0 bottom-0 flex max-h-[85dvh] flex-col bg-surface text-ink shadow-drawer',
            'rounded-t-panel border-t border-border pb-safe-bottom',
            'focus:outline-none',
            className,
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border p-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-title-2 text-ink">{title}</RadixDialog.Title>
              {description !== undefined ? (
                <RadixDialog.Description className="mt-1 text-support text-ink-muted">
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <IconButton label={closeLabel} variant="secondary" size="sm" className="shrink-0">
                <X {...decorativeIcon('sm')} />
              </IconButton>
            </RadixDialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

          {footer !== undefined ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border p-4">
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
